<template>
  <div
    class="dynamic-image"
    :class="[
      computedPropsClasses,
      {
        [`border--${props.variant}`]: state.loaded && !state.error,
      },
    ]"
  >
    <div v-if="computedDevMode" class="debugger">
      <span>{{ priority }}</span>
      <span>{{ loading }}</span>
    </div>
    <IntersectionObserver :options="computedIntersectionOptions" @intersect="onIntersect">
      <picture :id="ssrData?.elementId" class="picture" :class="computedPropsClasses">
        <template v-if="image && image?.id !== 'unknown'">
          <template
            v-if="props.loading !== 'lazy' || props.priority !== 'low' || state.intersected"
          >
            <source
              v-for="(item, index) in computedSources.toReversed()"
              :key="`source-${item.path}-${index}`"
              v-bind="item.rendition"
              :media="`(min-width: ${item.min}px)${item.max ? ` and (max-width: ${item.max}px)` : ''}`"
            />

            <img
              ref="imageRef"
              class="image"
              :class="[{ error: state.error, loaded: state.loaded }, computedPropsClasses]"
              :src="state.currentSource"
              :alt="computedAltText"
              :loading="props.loading"
              :fetchpriority="props.priority"
              width="100%"
              height="auto"
              @error="onError"
              @load="onLoaded"
            />
          </template>
        </template>

        <img
          v-else
          ref="imageRef"
          class="image error"
          :class="[{ loaded: state.loaded }, computedPropsClasses]"
          :src="errorImage"
          :alt="`Standaard afbeelding`"
          :loading="props.loading"
          :fetchpriority="props.priority === 'high' ? 'high' : undefined"
          width="100%"
          height="auto"
          @load="onError"
        />
      </picture>

      <small v-if="image.source && props.showSource" class="source">Bron: {{ image.source }}</small>

      <Transition :name="props.loading !== 'eager' ? 'fade' : 'instant'" mode="out-in">
        <Loader
          v-if="!state.loaded && !state.error"
          :loading="props.loading"
          :variant="props.variant"
        />
      </Transition>
    </IntersectionObserver>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { withBase } from 'ufo';
import useBreakpointsStore from '~/stores/breakpoints';
import { breakpoints } from '~~/configs/breakpoints.config';
import { imageFormats } from '~~/configs/images.config';
import IntersectionObserver from '~/components/IntersectionObserver.vue';
import type { Link } from '@unhead/vue';
import type {
  IBreakpoint,
  IFile,
  IImageLoadingSettings,
  IImageRendition,
  TBreakpoint,
  TImageFormat,
  TImageSize,
} from '~~/types/local_types';

const emit = defineEmits(['loaded', 'error']);
const { directusAssetsUrl } = useRuntimeConfig().public;
const breakpointsStore = useBreakpointsStore();
const appBaseURL = useRuntimeConfig().app.baseURL || '/';
const errorImage = withBase('/assets/images/default-image.webp', appBaseURL);

interface IProps extends IImageLoadingSettings {
  image: IFile;
  fit?: 'contain' | 'cover';
  showSource?: boolean;
  formats?: Array<TImageFormat>;
  renditions: IImageRendition[];
  fluid?: boolean;
  variant?: 'regular' | 'dark';
}

interface ISource {
  breakpoint: TBreakpoint;
  min: number;
  max: number;
  path: string;
  size: TImageSize;
  density: number;
  aspectRatio: string;
  rendition: {
    type: string;
    srcSet: string;
  };
}

const props = withDefaults(defineProps<IProps>(), {
  priority: 'low',
  loading: 'lazy',
  fit: 'cover',
  showSource: false,
  formats: () => ['jpg', 'webp'],
  fluid: false,
  variant: 'regular',
});

const state = reactive({
  error: false,
  currentSource: errorImage,
  initialized: false,
  intersected: false,
  loaded: false,
});

const imageRef = ref<HTMLImageElement>();

// 1. GENERATE STABLE IDENTIFIERS
const stableId = useId();

// Key now includes aspect ratios to ensure the placeholder correctly caches the shape
const componentFingerprint = computed(() => {
  const rendHash =
    props.renditions?.map((r) => `${r.breakpoint}${r.size}${r.aspectRatio}`).join('-') || 'no-rend';
  const imgIdentity = props.image?.id && props.image.id !== 'unknown' ? props.image.id : 'fallback';
  return `img-${imgIdentity}-${rendHash}`;
});

// 2. CSS GENERATION LOGIC
const generateCssData = (elementId: string, sources: ISource[]) => {
  let styleString = `#${elementId} {`;
  const filteredSources = sources
    .filter((source, index, array) => array.findIndex((s) => s.min === source.min) === index)
    .sort((a, b) => a.min - b.min);

  filteredSources.forEach((value) => {
    if (value.min === 0) {
      styleString += `aspect-ratio: ${value.aspectRatio};`;
    } else {
      styleString += `@media (min-width: ${value.min}px) { aspect-ratio: ${value.aspectRatio} }`;
    }
  });

  styleString += '}';
  return styleString;
};

// 3. SOURCE GENERATION (Helper for useAsyncData)
// Removed the !props.image check so aspect ratios are still calculated for the skeleton/placeholder
const getGeneratedSources = () => {
  const sources: ISource[] = [];
  if (!props.renditions?.length) return sources;

  const reversedBreakpoints = [...breakpoints].reverse();
  for (const breakpoint of reversedBreakpoints as IBreakpoint[]) {
    const rendition = props.renditions.find((r) => r.breakpoint === breakpoint.name);
    const prevEntry = sources[sources.length - 1];

    if (rendition) {
      const basePath =
        props.image?.id && props.image.id !== 'unknown'
          ? `${directusAssetsUrl}${props.image.id}`
          : '';
      const [aw, ah] = rendition.aspectRatio.split(/[x:]/);
      const size = rendition.size;

      const iso = props.image?.modifiedOn;

      let modifiedOn = '';
      if (iso) {
        const date = new Date(iso);

        modifiedOn =
          date.getFullYear() +
          '-' +
          String(date.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(date.getDate()).padStart(2, '0') +
          '-' +
          String(date.getHours()).padStart(2, '0') +
          '-' +
          String(date.getMinutes()).padStart(2, '0');
      }

      props.formats.forEach((format) => {
        const supported = imageFormats[format];
        const width = Math.round(size * supported.density);
        const height = Math.round(
          (size / parseInt(aw || '16')) * parseInt(ah || '9') * supported.density,
        );

        sources.push({
          breakpoint: breakpoint.name as TBreakpoint,
          min: breakpoint.min,
          max: prevEntry
            ? prevEntry.min - 1
            : ((breakpoints[breakpoints.length - 1] as IBreakpoint).max as number),
          size: rendition.size,
          density: supported.density,
          aspectRatio: `${aw || '16'}/${ah || '9'}`,
          path: basePath ? `${basePath}.${format}` : '',
          rendition: {
            type: supported.mime,
            srcSet: basePath
              ? `${basePath}.${format}?width=${width}&height=${height}&fit=${props.fit}${modifiedOn ? `&v=${modifiedOn}` : ''} ${supported.density}x`
              : '',
          },
        });
      });
    }
  }
  return sources;
};

// 4. ID & CSS TRANSPORT
const { data: ssrData } = await useAsyncData(
  `data-${componentFingerprint.value}`,
  () => {
    const elementId = `dynamic-image-${stableId}`;
    const sources = getGeneratedSources();
    const css = generateCssData(elementId, sources);
    return {
      elementId: String(elementId),
      css: String(css),
      sources,
    };
  },
  { server: true, immediate: true },
);

const computedSources = computed(() => ssrData.value?.sources || []);

// 5. INJECT HEAD STYLES
useHead({
  style: [
    {
      id: `style-${ssrData.value?.elementId}`,
      innerHTML: ssrData.value?.css || '',
    },
  ],
});

// LOGIC HELPERS
const computedAltText = computed(() => props.image?.description || 'AVROTROS');
const computedIntersectionOptions = computed(() => ({
  threshold: props.priority !== 'low' ? 0 : 0.01,
}));

const onIntersect = (e: { visible: boolean }) => {
  if (e.visible && !state.intersected) {
    state.intersected = true;
    if (imageRef.value?.complete) onLoaded();
  }
};

const initialize = () => {
  const sources = computedSources.value;
  if (sources.length > 0 && props.image?.id !== 'unknown') {
    let current;
    for (const b of breakpoints as IBreakpoint[]) {
      const found = sources.filter((s) => s.breakpoint === b.name);
      if (found.length > 0) current = found;
      if (b.name === breakpointsStore.currentBreakpoint?.name) break;
    }
    state.currentSource = current?.[0]?.rendition?.srcSet?.split(' ')[0] || errorImage;
  } else {
    state.currentSource = errorImage;
  }
  state.initialized = true;
};

const onLoaded = () => {
  state.loaded = true;
  emit('loaded');
};
const onError = () => {
  state.error = true;
  emit('error');
  state.currentSource = errorImage;
};

if (props.priority === 'high' && props.image?.id !== 'unknown') {
  const links = (ssrData.value?.sources || [])
    .filter((s) => s.path)
    .map((s) => ({
      rel: 'preload',
      href: s.rendition.srcSet.split(' ')[0],
      as: 'image',
    }));
  useHead({ link: links as Link[] });
}

onMounted(() => initialize());

const computedPropsClasses = computed(() => (props.fluid ? 'is--fluid' : ''));
const computedDevMode = computed(() => import.meta.dev);
</script>

<style scoped lang="postcss">
.dynamic-image {
  position: relative;
  &.is--fluid {
    display: block;
    height: 100%;
  }
  &:after {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    mix-blend-mode: multiply;
  }
  &.border--regular:after {
    border: 1px solid hsla(var(--color-primary-950), 0.075);
  }
  &.border--dark:after {
    border: 1px solid hsla(var(--color-primary-50), 0.075);
    mix-blend-mode: lighten;
  }
}
.picture {
  display: block;
  line-height: 0;
  overflow: hidden;
  will-change: aspect-ratio, layout;
  transition: all 250ms ease;
  &.is--fluid {
    display: contents;
  }
}
.image {
  width: 100%;
  overflow: hidden;
  &.is--fluid {
    position: absolute;
    height: 100%;
    object-fit: cover;
    object-position: top center;
  }
  &.error {
    object-fit: cover;
    height: 100%;
  }
}
.source {
  position: absolute;
  bottom: var(--unit-12);
  right: var(--unit-12);
  padding: var(--unit-2) var(--unit-6);
  border-radius: var(--unit-2);
  background-color: hsla(var(--color-primary-950), 0.7);
  color: hsl(var(--color-neutral-white));
  @media (--lg-viewport) {
    bottom: var(--unit-16);
    right: var(--unit-16);
  }
}
.debugger {
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 5px;
  top: 0;
  right: 0;
  padding: 5px;
  background: rgba(255, 255, 255, 0.7);
  font-family: monospace;
  font-size: 10px;
  z-index: 5;
}
</style>
